import { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  TextField,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Button,
  Typography,
  Card,
  InputAdornment,
  CircularProgress,
  Avatar,
  Snackbar,
  Alert,
  Modal,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import VerifiedIcon from "@mui/icons-material/Verified";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/Send";
import AdminApiModule from "../../components/AdminApi/Api/api";
import CenterRegistrationApi from "../../components/centerRegistration/Api/api";
import { useSelector } from "react-redux";
import { IndianStates } from "../../assets/IndianStates";
import CopyButton from "../../components/ServicesStatus/ClipbaordButton";
import { BASE_URL } from "../../components/config/config";
import { saveAs } from "file-saver";

// Import permission utilities
import {
  canAddTicket,
  canBulkUploadTicket,
  canUpdateTicket,
  hasFullAccess,
} from "../../utility/permissionUtils";

const theme = createTheme({
  overrides: {
    CssBaseline: {
      "@global": {
        "*::-webkit-scrollbar": {
          width: "5px",
        },
        "*::-webkit-scrollbar-track": {
          background: "#E4EFEF",
        },
        "*::-webkit-scrollbar-thumb": {
          background: "#1D388F61",
          borderRadius: "2px",
        },
      },
    },
  },
});

// Modal css
const modalStyle = {
  position: "absolute",
  top: "40%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 700,
  bgcolor: "background.paper",
  border: "2px solid gray",
  boxShadow: 44,
  p: 4,
  borderRadius: 4,
};

// List render
const listStyle = {
  width: "100%",
  bgcolor: "background.paper",
};

const AVAILABLE_TIME_PREFERENCES = [
  "IMMEDIATELY",
  "WITHIN_24_HOURS",
  "SPECIFIC_DATE_AND_TIME",
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 16;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
      display: "flex",
    },
  },
};

const EXCEL_TEMPLATE_COLUMNS = [
  "CenterQr",
  "Client Id",
  "Service provided for",
  "Specific requirement",
  "service_provided_client_id",
  "Country code",
  "Mobile number",
  "Customer name",
  "House number",
  "Locality",
  "City",
  "State",
  "Pincode",
  "Alternate number",
  "Country",
  "Time preference type",
  "Specific date and time",
  "Ticket price",
  "Offer code",
  "Is paid ticket",
  "ticketCreatedBy",
  "service_provided_due_amount",
];

const AddClientTicket = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const reduxState = useSelector((state) => state.userReducer);
  const globalState = useSelector((state) => state.AdminReducer);

  // Get user permissions
  const userPermissions = globalState?.adminDetails?.role_id?.permissions;

  // Check permissions
  const hasAddTicketPermission =
    canAddTicket(userPermissions) || hasFullAccess(userPermissions);
  const hasBulkUploadPermission =
    canBulkUploadTicket(userPermissions) || hasFullAccess(userPermissions);
  const hasUpdateTicketPermission =
    canUpdateTicket(userPermissions) || hasFullAccess(userPermissions);

  const [formData, setFormData] = useState({
    serviceRequired: "",
    specificRequirement: "",
    name: "",
    phoneNumber: "",
    alternatePhoneNumber: "",
    houseNumber: "",
    locality: "",
    city: "",
    state: "",
    country: "INDIA",
    pincode: "",
    timePreference: "",
    authorizedForClient: "",
    offerCode: "ONIT2026",
    is_paid: true,
    closing_ticket_price: "",
    admin_remarks: "",
    specific_date_time: new Date(),
    service_provided_client_id: "",
    service_provided_due_amount: "0",
  });

  const [state, setState] = useState({
    toast: {
      open: false,
      msg: "",
      duration: 5000,
      type: "",
    },
    isFormValid: false,
    servicesList: [],
    clientsList: [],
    isRegistering: false,
    isEditDetailsExists: false,
    doesTicketExists: false,
    TicketModalOpen: false,
    TicketModalData: [],
    bulkUploadMode: false,
    selectedFile: null,
    fileName: "",
    uploadedTickets: [],
    rejectedTickets: [],
    bulkUploadClientId: "",
    bulkUploadClientName: "",
    bulkUploadClientIdField: "",
    bulkUploadDueAmount: "0",
    bulkUploadErrors: {},
    bulkUploadStats: {
      total: 0,
      accepted: 0,
      rejected: 0,
    },
    showErrorDetails: false,
  });

  useEffect(() => {
    getAllServicesExists();
    getAllClients();

    const editTicketId = new URLSearchParams(location.search).get("edit");

    if (editTicketId != null) {
      setState((prev) => ({ ...prev, isEditDetailsExists: true }));
      getTicketDetails(editTicketId);
    }
  }, [location.search]);

  const onChangeSpecificDateAndTime = (newValue) => {
    setFormData((prev) => ({ ...prev, specific_date_time: newValue }));
  };

  const getTicketObjIdForEdit = () => {
    return new URLSearchParams(location.search).get("edit");
  };

  const getTicketDetails = async (editTicketId) => {
    const payload = {
      ticket_obj_id: editTicketId,
    };

    try {
      const res = await new AdminApiModule().getAllTickets(payload);
      if (res?.data?.length) {
        setState((prev) => ({ ...prev, doesTicketExists: true }));
        updateTicketDetails(res?.data?.[0]);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const updateTicketDetails = (ticketDetails) => {
    setFormData({
      serviceRequired: ticketDetails?.service_provided_for?._id,
      specificRequirement: ticketDetails?.specific_requirement,
      name: ticketDetails?.personal_details?.name,
      phoneNumber:
        ticketDetails?.personal_details?.primary_phone?.mobile_number,
      alternatePhoneNumber:
        ticketDetails?.personal_details?.alternate_phone?.mobile_number,
      houseNumber: ticketDetails?.address_details?.house_number,
      locality: ticketDetails?.address_details?.locality,
      city: ticketDetails?.address_details?.city,
      state: ticketDetails?.address_details?.state,
      country: "INDIA",
      pincode: ticketDetails?.address_details?.pincode,
      timePreference: ticketDetails?.time_preference?.time_preference_type,
      authorizedForClient: ticketDetails?.authorized_client_id?._id,
      offerCode: "ONIT2026",
      is_paid: ticketDetails?.admin_setting?.is_paid,
      closing_ticket_price: ticketDetails?.closing_ticket_price,
      admin_remarks: ticketDetails?.remarks?.admin_remarks,
      specific_date_time: ticketDetails?.time_preference?.specific_date_time,
      service_provided_client_id:
        ticketDetails?.service_provided_client_id || "",
      service_provided_due_amount:
        ticketDetails?.service_provided_due_amount || "0",
    });
  };

  const getAllServicesExists = async () => {
    try {
      const res = await new CenterRegistrationApi().getAllActiveServices();
      setState((prev) => ({ ...prev, servicesList: res.data }));
    } catch (err) {
      console.log(err);
    }
  };

  const getAllClients = async () => {
    try {
      const res = await new AdminApiModule().getAllClients();
      let parsedData = [];
      if (res && res.data && res.data.length > 0) {
        parsedData = res.data;
      }
      setState((prev) => ({ ...prev, clientsList: parsedData }));
    } catch (err) {
      console.log(err);
    }
  };

  const showDialog = (open, type, msg) => {
    setState((prev) => ({
      ...prev,
      toast: {
        open: open,
        msg: msg,
        duration: 5000,
        type: type,
      },
    }));
  };

  const closeDialog = (open = false) => {
    setState((prev) => ({
      ...prev,
      toast: {
        open: open,
        msg: "",
        duration: 5000,
        type: "",
      },
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeCheckBox = (e) => {
    setFormData((prev) => ({ ...prev, is_paid: e.target.checked }));
  };

  const handleRegister = async () => {
    // Check permissions before proceeding
    if (state.isEditDetailsExists && !hasUpdateTicketPermission) {
      showDialog(true, "error", "You don't have permission to update tickets");
      return;
    }

    if (!state.isEditDetailsExists && !hasAddTicketPermission) {
      showDialog(true, "error", "You don't have permission to add tickets");
      return;
    }

    const {
      serviceRequired,
      specificRequirement,
      name,
      phoneNumber,
      alternatePhoneNumber,
      houseNumber,
      locality,
      city,
      state: formState, // Renamed to avoid conflict with component state
      country,
      pincode,
      timePreference,
      authorizedForClient,
      offerCode,
    } = formData;

    if (
      !(
        serviceRequired &&
        name &&
        phoneNumber &&
        houseNumber &&
        pincode &&
        timePreference &&
        offerCode
      )
    ) {
      setState((prev) => ({ ...prev, isFormValid: true }));
      showDialog(true, "error", "Please fill in all required details");
      return;
    }

    setState((prev) => ({ ...prev, isRegistering: true }));

    if (formData.timePreference == "SPECIFIC_DATE_AND_TIME") {
      if (!formData.specific_date_time) {
        showDialog(true, "error", "Please fill in Specific date and time");
        setState((prev) => ({ ...prev, isRegistering: false }));
        return;
      }
    }

    let payload = {
      personal_details: {
        primary_phone: {
          country_code: "+91",
          mobile_number: phoneNumber,
        },
        alternate_phone: {
          country_code: alternatePhoneNumber.length > 0 ? "+91" : "",
          mobile_number: alternatePhoneNumber,
        },
        name: name,
      },
      specific_requirement: specificRequirement,
      service_provided_for: serviceRequired,
      address_details: {
        house_number: houseNumber,
        locality: locality,
        city: city,
        state: formState,
        pincode: pincode,
        country: country,
      },
      time_preference: {
        time_preference_type: timePreference,
        specific_date_time: formData.specific_date_time,
      },
      offers_applied: {
        offer_code: offerCode,
      },
      create_name: globalState?.adminDetails?.user_name,
    };

    if (authorizedForClient) {
      payload["authorized_client_id"] = authorizedForClient;
      payload["service_provided_client_id"] =
        formData.service_provided_client_id;
      payload["service_provided_due_amount"] =
        formData.service_provided_due_amount;
    }

    if (state.isEditDetailsExists) {
      payload.ticket_obj_id = getTicketObjIdForEdit();
      payload.closing_ticket_price = formData.closing_ticket_price || "";
      payload.admin_remarks = formData.admin_remarks || "";
      delete payload.is_paid;
      delete payload.authorized_client_id;

      try {
        const res = await new AdminApiModule().updateTicket(payload);
        showDialog(true, "success", "Ticket updated successfully");
        setState((prev) => ({ ...prev, isRegistering: false }));
        navigate("/Tickets");
      } catch (err) {
        console.log(err);
        showDialog(true, "error", err?.response?.data?.message);
        setState((prev) => ({ ...prev, isRegistering: false }));
      }
      return;
    }

    try {
      const res = await new AdminApiModule().createTicket(payload);
      const TicketData = res?.data;
      showDialog(true, "success", "Ticket Created successfully");

      setState((prev) => ({
        ...prev,
        TicketModalData: TicketData,
      }));

      handleResetValues();

      setTimeout(() => {
        showDialog(true, "info", "Add more ticket");
        setState((prev) => ({ ...prev, TicketModalOpen: true }));
      }, 2000);
    } catch (err) {
      console.log(err);
      setState((prev) => ({ ...prev, isRegistering: false }));
      showDialog(true, "error", err?.response?.data?.message);
    }
  };

  const handleResetValues = () => {
    setState((prev) => ({
      ...prev,
      isRegistering: false,
    }));

    setFormData({
      serviceRequired: "",
      specificRequirement: "",
      name: "",
      phoneNumber: "",
      alternatePhoneNumber: "",
      houseNumber: "",
      locality: "",
      city: "",
      state: "",
      country: "INDIA",
      pincode: "",
      timePreference: "",
      offerCode: "",
      authorizedForClient: "",
      admin_remarks: "",
      isFormValid: false,
      service_provided_client_id: "",
      service_provided_due_amount: "0",
    });
  };

  const openWhatsapp = (number, id, name) => {
    if (number) {
      const formattedPhoneNumber = number.replace(/[^\d]/g, "");
      const a = `https://app.onit.services/#/ServiceStatus/?${id}`;
      const message = `Dear ${name} request Onit has been created, Please click on the link to check status: ${a} Download the App from playstore and get more Benefits and Offers`;

      const encodedMessage = encodeURIComponent(message);
      window.open(
        `https://web.whatsapp.com/send?phone=${formattedPhoneNumber}&text=${encodedMessage}`,
        "_blank"
      );
    } else {
      console.error("Phone number is not defined or invalid");
    }
  };

  const handleClose = () => {
    setState((prev) => ({ ...prev, TicketModalOpen: false }));
  };

  // Bulk upload functions
  const handleBulkUploadToggle = () => {
    if (!hasBulkUploadPermission) {
      showDialog(true, "error", "You don't have permission for bulk upload");
      return;
    }
    setState((prev) => ({
      ...prev,
      bulkUploadMode: !prev.bulkUploadMode,
      selectedFile: null,
      fileName: "",
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (!validTypes.includes(file.type)) {
        showDialog(true, "error", "Please upload a valid CSV or Excel file");
        return;
      }

      setState((prev) => ({
        ...prev,
        selectedFile: file,
        fileName: file.name,
      }));
    }
  };

  const downloadTemplate = () => {
    if (!hasBulkUploadPermission) {
      showDialog(
        true,
        "error",
        "You don't have permission to download template"
      );
      return;
    }
    const header = EXCEL_TEMPLATE_COLUMNS.join(",");
    const exampleData = [
      "",
      "CL-86",
      "AIR CONDITIONER - AND GEYSER",
      "Fix leakage",
      "1234567890",
      "",
      "8285714897",
      "Dev",
      "123A",
      "Green Street",
      "New Delhi",
      "Delhi",
      "110001",
      "6284714797",
      "INDIA",
      "IMMEDIATELY",
      "",
      "1000",
      "ONIT2026",
      "Yes",
      "IMPORT_VIA_EXCEL",
      "500",
    ].join(",");
    const csvContent = [header, exampleData].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "ticket_upload_template.csv");
  };

  const handleBulkSubmit = async () => {
    if (!hasBulkUploadPermission) {
      showDialog(true, "error", "You don't have permission for bulk upload");
      return;
    }

    const { selectedFile, bulkUploadClientId } = state;
    if (!selectedFile) {
      showDialog(true, "error", "Please select a file to upload");
      return;
    }
    if (!bulkUploadClientId) {
      showDialog(true, "error", "Please select a client for bulk upload");
      return;
    }
    setState((prev) => ({ ...prev, isRegistering: true }));
    const formData = new FormData();
    formData.append("ticketsCsvFile", selectedFile);
    try {
      const response = await fetch(
        `${BASE_URL}ticket/createBulkTickets?clientId=${bulkUploadClientId}`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (result.success === 1) {
        setState((prev) => ({
          ...prev,
          uploadedTickets: result.data?.acceptedTicketDetails || [],
          rejectedTickets: result.data?.rejectedTicketDetails || [],
          bulkUploadErrors: result.data?.errorRowWise || {},
          bulkUploadStats: {
            total: result.data?.recievedTickets || 0,
            accepted: result.data?.acceptedTickets || 0,
            rejected: result.data?.rejectedTickets || 0,
          },
          selectedFile: null,
          fileName: "",
          isRegistering: false,
        }));

        const message = result.data.doesErrorExists
          ? `Processed ${result.data.recievedTickets} tickets: ${result.data.acceptedTickets} accepted, ${result.data.rejectedTickets} rejected`
          : `Successfully uploaded ${result.data.acceptedTickets} tickets!`;

        showDialog(
          true,
          result.data.doesErrorExists ? "warning" : "success",
          message
        );
      } else {
        showDialog(true, "error", result.message || "Upload failed");
        setState((prev) => ({ ...prev, isRegistering: false }));
      }
    } catch (error) {
      console.error("Bulk Upload Error:", error);
      showDialog(true, "error", "Something went wrong during upload.");
      setState((prev) => ({ ...prev, isRegistering: false }));
    }
  };

  const toggleErrorDetails = () => {
    setState((prev) => ({
      ...prev,
      showErrorDetails: !prev.showErrorDetails,
    }));
  };

  const handleBulkUploadClientChange = (e) => {
    const selectedClient = state.clientsList.find(
      (client) => client._id === e.target.value
    );
    setState((prev) => ({
      ...prev,
      bulkUploadClientId: selectedClient?.client_id || "",
    }));
  };

  const getPincodeFromLocation = () => {
    // Implement geolocation to fetch pincode
    // For now, just a placeholder
    showDialog(true, "info", "Pincode fetch functionality to be implemented");
  };

  // Permission-based rendering checks
  if (state.isEditDetailsExists && !state.doesTicketExists) {
    return <>Page not found !!!</>;
  }

  if (state.isEditDetailsExists && !hasUpdateTicketPermission) {
    return (
      <div className="flex justify-center items-center h-64">
        <Typography variant="h6" color="error">
          No permission to update request. Please contact admin.
        </Typography>
      </div>
    );
  }

  if (!state.isEditDetailsExists && !hasAddTicketPermission) {
    return (
      <div className="flex justify-center items-center h-64">
        <Typography variant="h6" color="error">
          No permission to add request. Please contact admin.
        </Typography>
      </div>
    );
  }

  return (
    <div className="pb-8">
      {/* Ticket creation modal code */}
      <Modal
        keepMounted
        open={state.TicketModalOpen}
        onClose={handleClose}
        aria-labelledby="keep-mounted-modal-title"
        aria-describedby="keep-mounted-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography
            id="keep-mounted-modal-title"
            variant="h6"
            component="h2"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <VerifiedIcon color="success" fontSize="large" />
            Hurray! Your Request has been created
          </Typography>
          <Typography id="keep-mounted-modal-description" sx={{ mt: 2 }}>
            {/* List code  */}
            <List sx={listStyle} component="nav" aria-label="mailbox folders">
              <ListItem button>
                <ListItemText primary="Ticket Id :-" />{" "}
                <Typography> {state.TicketModalData?.ticket_id} </Typography>
              </ListItem>
              <Divider />
              <ListItem button divider>
                <ListItemText primary="Ticket Status :-" />{" "}
                <Typography>
                  {" "}
                  {state.TicketModalData?.ticket_status}{" "}
                </Typography>
              </ListItem>
              <ListItem button>
                <ListItemText primary="Created At :-" />{" "}
                <Typography> {state.TicketModalData?.createdAt} </Typography>
              </ListItem>
              <Divider light />
              <ListItem button>
                <ListItemText primary="Tracking Link :-" />{" "}
                <Typography>
                  <CopyButton
                    webLink={`https://app.onit.services/#/ServiceStatus/?${state.TicketModalData?._id}`}
                  />
                  <Link
                    style={{ marginLeft: "17px" }}
                    to={`ServiceStatus/?${state.TicketModalData?._id}`}
                  >
                    Click me
                  </Link>
                </Typography>
              </ListItem>
              <ListItem>
                <IconButton
                  onClick={() =>
                    openWhatsapp(
                      state.TicketModalData?.personal_details?.primary_phone
                        ?.mobile_number,
                      state.TicketModalData?._id,
                      state.TicketModalData?.personal_details?.name
                    )
                  }
                >
                  <WhatsAppIcon
                    sx={{ color: "green", fontSize: 30, padding: "1px" }}
                  />
                </IconButton>
              </ListItem>
            </List>
          </Typography>
        </Box>
      </Modal>

      {state.toast.open && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={state.toast.open}
          autoHideDuration={state.toast.duration}
          onClose={closeDialog}
        >
          <Alert
            onClose={closeDialog}
            severity={state.toast.type}
            sx={{ width: "100%" }}
          >
            {state.toast.msg}
          </Alert>
        </Snackbar>
      )}

      {/* Main Form */}
      <Typography variant="h4" className="font-bold mb-6 text-center">
        {state.isEditDetailsExists ? "Update Request" : "Add New Request"}
      </Typography>

      {/* Bulk Upload Section - Only show if user has permission */}
      {hasBulkUploadPermission && (
        <Card className="rounded-lg shadow-md p-6 mb-6">
          <Typography variant="h6" className="font-bold mb-4">
            Bulk Upload Options
          </Typography>
          <div className="flex flex-wrap gap-4 mb-4">
            <Button
              variant={state.bulkUploadMode ? "contained" : "outlined"}
              color="primary"
              onClick={handleBulkUploadToggle}
              startIcon={<CloudUploadIcon />}
            >
              {state.bulkUploadMode
                ? "Cancel Bulk Upload"
                : "Bulk Upload via Excel"}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={downloadTemplate}
              startIcon={<DownloadIcon />}
            >
              Download Template
            </Button>
          </div>

          {state.bulkUploadMode && (
            <div className="mt-4 space-y-4">
              <FormControl fullWidth>
                <InputLabel id="bulk-upload-client-label">
                  Select Client for Bulk Upload
                </InputLabel>
                <Select
                  labelId="bulk-upload-client-label"
                  value={
                    state.clientsList.find(
                      (client) => client.client_id === state.bulkUploadClientId
                    )?._id || ""
                  }
                  onChange={handleBulkUploadClientChange}
                  label="Select Client for Bulk Upload"
                  required
                >
                  {state.clientsList?.map((client) => (
                    <MenuItem key={client._id} value={client._id}>
                      {client.client_name} (CLIENT ID: {client.client_id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <div className="flex flex-wrap items-center gap-4">
                <input
                  accept=".xlsx,.xls,.csv"
                  style={{ display: "none" }}
                  id="bulk-upload-file"
                  type="file"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                />
                <label htmlFor="bulk-upload-file">
                  <Button
                    variant="contained"
                    component="span"
                    color="primary"
                    startIcon={<CloudUploadIcon />}
                  >
                    Choose File
                  </Button>
                </label>
                {state.fileName && (
                  <Typography variant="body2" className="ml-2">
                    {state.fileName}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBulkSubmit}
                  disabled={!state.selectedFile || !state.bulkUploadClientId}
                  startIcon={
                    state.isRegistering ? <CircularProgress size={20} /> : null
                  }
                >
                  {state.isRegistering ? "Processing..." : "Upload & Process"}
                </Button>
              </div>
              <Typography variant="caption" color="textSecondary">
                Note: Upload Excel file with proper format. Download template
                for reference.
              </Typography>
            </div>
          )}

          {state.rejectedTickets.length > 0 && (
            <div className="mt-4 p-4 border border-red-200 rounded-lg">
              <div className="flex justify-between items-center">
                <Typography variant="h6" color="error">
                  Upload Summary: {state.bulkUploadStats.accepted} Accepted,{" "}
                  {state.bulkUploadStats.rejected} Rejected
                </Typography>
                <Button onClick={toggleErrorDetails} size="small" color="error">
                  {state.showErrorDetails ? "Hide Details" : "Show Details"}
                </Button>
              </div>

              {state.showErrorDetails && (
                <div className="mt-2 max-h-80 overflow-auto">
                  <TableContainer component={Paper}>
                    <Table size="small" aria-label="error details">
                      <TableHead>
                        <TableRow className="bg-red-100">
                          <TableCell>Row</TableCell>
                          <TableCell>Customer</TableCell>
                          <TableCell>Service Provider Client Id</TableCell>
                          <TableCell>Errors</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {state.rejectedTickets.map((ticket, index) => (
                          <TableRow key={index}>
                            <TableCell>{ticket.row}</TableCell>
                            <TableCell>
                              {ticket.customer_name || "N/A"}
                            </TableCell>
                            <TableCell>
                              {ticket.service_provided_client_id || "N/A"}
                            </TableCell>
                            <TableCell className="text-red-500">
                              {Array.isArray(ticket.errors)
                                ? ticket.errors.join(", ")
                                : JSON.stringify(ticket.errors)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              )}
            </div>
          )}

          {state.uploadedTickets.length > 0 && (
            <div className="mt-4 p-4 border border-green-200 rounded-lg">
              <Typography variant="h6" className="text-green-800">
                Successfully Created Tickets ({state.uploadedTickets.length})
              </Typography>
              <div className="mt-2 max-h-80 overflow-auto">
                <TableContainer component={Paper}>
                  <Table size="small" aria-label="success details">
                    <TableHead className="bg-green-100">
                      <TableRow>
                        <TableCell>Ticket ID</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Service Provider Client Id</TableCell>
                        <TableCell>Ticket Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {state.uploadedTickets.map((ticket, index) => (
                        <TableRow key={index}>
                          <TableCell>{ticket.ticket_id}</TableCell>
                          <TableCell>{ticket.customer_name}</TableCell>
                          <TableCell>
                            {ticket.service_provided_client_id || "N/A"}
                          </TableCell>
                          <TableCell>₹{ticket.ticket_price}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Main Form */}
      <Card className="rounded-lg shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Service Required */}
          <FormControl fullWidth>
            <InputLabel id="service-required-label">
              Service You Required *
            </InputLabel>
            <Select
              labelId="service-required-label"
              id="serviceRequired"
              name="serviceRequired"
              value={formData.serviceRequired}
              onChange={handleChange}
              label="Service You Required *"
              error={state.isFormValid && !formData.serviceRequired}
            >
              <MenuItem value="">
                <em>Select a service</em>
              </MenuItem>
              {state.servicesList?.map((service) => (
                <MenuItem key={service._id} value={service._id}>
                  <div className="flex items-center">
                    <Avatar
                      alt={service?.service_name}
                      src={service?.image}
                      sx={{ width: 29, height: 29, marginRight: "14px" }}
                    />
                    <span className="truncate">{service.service_name}</span>
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Specific Requirement */}
          <TextField
            fullWidth
            label="Specific Requirement"
            name="specificRequirement"
            value={formData.specificRequirement}
            onChange={handleChange}
            multiline
            rows={2}
          />

          {/* Name */}
          <TextField
            fullWidth
            label="Name (Contact Person) *"
            name="name"
            value={formData.name}
            disabled={state.isEditDetailsExists}
            onChange={handleChange}
            error={state.isFormValid && !formData.name}
            helperText={state.isFormValid && !formData.name ? "Required" : ""}
          />

          {/* Phone Number */}
          <TextField
            fullWidth
            label="Phone Number *"
            name="phoneNumber"
            value={formData.phoneNumber}
            disabled={state.isEditDetailsExists}
            onChange={handleChange}
            error={state.isFormValid && !formData.phoneNumber}
            helperText={
              state.isFormValid && !formData.phoneNumber ? "Required" : ""
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">+91</InputAdornment>
              ),
            }}
            inputProps={{
              maxLength: 10,
            }}
          />

          {/* Alternate Phone Number */}
          <TextField
            fullWidth
            label="Alternate Phone Number"
            name="alternatePhoneNumber"
            value={formData.alternatePhoneNumber}
            disabled={state.isEditDetailsExists}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">+91</InputAdornment>
              ),
            }}
            inputProps={{
              maxLength: 10,
            }}
            placeholder={
              formData.alternatePhoneNumber === "" ? "Not Provided" : ""
            }
          />

          {/* House Number */}
          <TextField
            fullWidth
            label="House Number / Street *"
            name="houseNumber"
            value={formData.houseNumber}
            onChange={handleChange}
            error={state.isFormValid && !formData.houseNumber}
            helperText={
              state.isFormValid && !formData.houseNumber ? "Required" : ""
            }
          />

          {/* Locality */}
          <TextField
            fullWidth
            label="Locality *"
            name="locality"
            value={formData.locality}
            onChange={handleChange}
            error={state.isFormValid && !formData.locality}
            helperText={
              state.isFormValid && !formData.locality ? "Required" : ""
            }
          />

          {/* City */}
          <TextField
            fullWidth
            label="City *"
            name="city"
            value={formData.city}
            onChange={handleChange}
            error={state.isFormValid && !formData.city}
            helperText={state.isFormValid && !formData.city ? "Required" : ""}
          />

          {/* State */}
          <FormControl fullWidth>
            <InputLabel id="state-label">State *</InputLabel>
            <Select
              labelId="state-label"
              id="state"
              name="state"
              value={formData.state}
              onChange={handleChange}
              label="State *"
              error={state.isFormValid && !formData.state}
            >
              <MenuItem value="">
                <em>Select state</em>
              </MenuItem>
              {IndianStates?.map((state) => (
                <MenuItem key={state.value} value={state.value}>
                  {state.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Country */}
          <TextField
            fullWidth
            label="Country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            disabled
          />

          {/* Pincode */}
          <FormControl fullWidth>
            <TextField
              label="Pincode *"
              name="pincode"
              value={formData.pincode}
              onChange={handleChange}
              error={state.isFormValid && !formData.pincode}
              helperText={
                state.isFormValid && !formData.pincode ? "Required" : ""
              }
            />
          </FormControl>

          {/* Time Preference */}
          <FormControl fullWidth>
            <InputLabel id="time-preference-label">
              Time Preference *
            </InputLabel>
            <Select
              labelId="time-preference-label"
              id="timePreference"
              name="timePreference"
              value={formData.timePreference}
              onChange={handleChange}
              label="Time Preference *"
              error={state.isFormValid && !formData.timePreference}
            >
              <MenuItem value="">
                <em>Select time preference</em>
              </MenuItem>
              {AVAILABLE_TIME_PREFERENCES?.map((pref) => (
                <MenuItem key={pref} value={pref}>
                  {pref.replace(/_/g, " ")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Specific Date and Time */}
          {formData.timePreference === "SPECIFIC_DATE_AND_TIME" && (
            <div className="col-span-1 md:col-span-2">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Select Date and Time *"
                  value={formData.specific_date_time}
                  onChange={onChangeSpecificDateAndTime}
                  minDate={new Date()}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth helperText="" />
                  )}
                />
              </LocalizationProvider>
            </div>
          )}

          {/* Offer Code */}
          <TextField
            fullWidth
            label="Offer Code *"
            name="offerCode"
            value={formData.offerCode}
            onChange={handleChange}
            error={state.isFormValid && !formData.offerCode}
            helperText={
              state.isFormValid && !formData.offerCode ? "Required" : ""
            }
          />

          {/* Client Authorization */}
          {!state.isEditDetailsExists && (
            <>
              <FormControl fullWidth>
                <InputLabel id="client-auth-label">
                  Authorize for client (optional)
                </InputLabel>
                <Select
                  labelId="client-auth-label"
                  id="authorizedForClient"
                  name="authorizedForClient"
                  value={formData.authorizedForClient}
                  onChange={handleChange}
                  label="Authorize for client (optional)"
                >
                  <MenuItem value="">
                    <em>Select client</em>
                  </MenuItem>
                  {state.clientsList?.map((client) => (
                    <MenuItem key={client._id} value={client._id}>
                      {client.client_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {formData.authorizedForClient && (
                <>
                  <TextField
                    fullWidth
                    label="Client ID"
                    name="service_provided_client_id"
                    value={formData.service_provided_client_id}
                    onChange={handleChange}
                  />

                  <TextField
                    fullWidth
                    label="DUE Amount"
                    name="service_provided_due_amount"
                    type="number"
                    value={formData.service_provided_due_amount}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                  />
                </>
              )}
            </>
          )}

          {/* Admin Fields for Edit Mode */}
          {state.isEditDetailsExists && (
            <>
              <TextField
                fullWidth
                label="Closing price"
                name="closing_ticket_price"
                type="number"
                value={formData.closing_ticket_price}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Admin remarks"
                name="admin_remarks"
                value={formData.admin_remarks}
                onChange={handleChange}
                placeholder={
                  formData.admin_remarks === undefined ? "No Comments" : ""
                }
              />
            </>
          )}

          {/* Submit Button */}
          <div className="col-span-1 md:col-span-2 flex justify-center mt-4">
            <Button
              onClick={handleRegister}
              disabled={state.isRegistering}
              variant="contained"
              color="primary"
              size="large"
              endIcon={
                state.isRegistering ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              className="w-full md:w-auto"
            >
              {state.isRegistering
                ? "Processing..."
                : state.isEditDetailsExists
                ? "Update Request"
                : "Submit Request"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AddClientTicket;
